import { useEffect } from "react"
import { useLocation } from "react-router-dom"

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    gtag: (...args: any[]) => void
    dataLayer: unknown[]
  }
}

/**
 * Injects a Google Analytics 4 tag and fires a page_view on every route change.
 * Renders nothing — mount inside any layout component.
 *
 * Usage:
 *   <GoogleAnalytics id="G-XXXXXXXXXX" />
 */
export function GoogleAnalytics({ id }: { id: string }) {
  const location = useLocation()

  // Load script once per measurement ID (idempotent)
  useEffect(() => {
    if (document.querySelector(`script[data-ga="${id}"]`)) return

    // Set up dataLayer and the gtag function synchronously so queued
    // calls below are available before the async script finishes loading.
    window.dataLayer = window.dataLayer || []
    window.gtag = function gtag() {
      // arguments object required by gtag — cannot use rest params here
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer.push(arguments)
    }
    window.gtag("js", new Date())

    const script = document.createElement("script")
    script.async = true
    script.src = `https://www.googletagmanager.com/gtag/js?id=${id}`
    script.setAttribute("data-ga", id)
    document.head.appendChild(script)
  }, [id])

  // Fire page_view on every navigation (including initial mount)
  useEffect(() => {
    if (typeof window.gtag !== "function") return
    window.gtag("config", id, {
      page_path: location.pathname + location.search,
    })
  }, [id, location.pathname, location.search])

  return null
}
